import React, { Component } from "react";
import Spinner from "../../lib/spinner";
import StripeConnection from "./stripe_connection";
import BalanceIndicatorWrapper from "../../base/balance_indicator/balance_indicator_wrapper";
import PageWrapper from "../../lib/page-wrapper";
import messageType from "../../../constants/message_types";
import get from "lodash/get";
import some from "lodash/some";
import { HAS_SEEN_MONETIZATION_INTRO } from "../../../constants/user_attributes";
import { Button, Grid, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { If } from "react-extras";
import ExternalLink from "../../lib/external_link";
import "./monetization.scss";
import Divider from "../../lib/divider";
import parallelLimit from "async/parallelLimit";
import WarningPanel from "../../lib/warning_panel";
import ContextMenu from "../../lib/context_menu";
import TransactionTable from "../../lib/transaction_table";
import userAPI from "../../../api/user";
import {
  CreatorInsertionCredit,
  PendingInvoiceTransaction,
  RecurringDonationCredit,
  SubscriptionSurchargeCredit,
  TipCredit,
} from "../../../constants/transactions";

class MonetizationPage extends Component {
  state = {
    stripeStatus: "",
    introMoreInfo: false,
    isPutting: false,
  };

  componentDidMount() {
    const { getShowCampaigns } = this.props;

    if (!this.hasSeenIntro()) {
      this.props.showIntroModal();
    }
    userAPI.userStripeStatus(this.props.user).then(resp => {
      if (resp.status === 200) {
        resp.json().then(json => {
          const status = get(json, "status", "");
          this.setState({ stripeStatus: status });
        });
      }
    });

    this.props.fetchUser(this.props.user.uuid, this.props.user.authToken);
    this.props.getTransactionsForUser();
    this.props.fetchShowsForUser(this.props.user).then(resp => {
      if (resp.status === 200) {
        const shows = resp.json;
        shows.forEach(show => {
          if (show.advertisementSettings) {
            getShowCampaigns(show.uuid);
          }
        });
      }
    });
  }

  hasSeenIntro = () => {
    return get(this.props.user, ["userAttributes", HAS_SEEN_MONETIZATION_INTRO]) === "true";
  };

  creatorPayments = () => {
    const userTxnTypes = new Set([
      SubscriptionSurchargeCredit,
      CreatorInsertionCredit,
      TipCredit,
      PendingInvoiceTransaction,
      RecurringDonationCredit,
    ]);
    return this.props.transactions.userTransactions.filter(transaction =>
      userTxnTypes.has(transaction.type)
    );
  };

  hasNoShowsWithTipsEnabled = () => {
    if (this.props.isLoading) {
      return false;
    }
    const userShows = Object.keys(this.props.shows).filter(
      showUUID => this.props.shows[showUUID].userUUID === this.props.user.uuid
    );
    if (!userShows.length) {
      return false;
    }
    return !userShows.some(
      showUUID =>
        get(this.props.shows, [showUUID, "subscriptionDetails", "recurringDonationsEnabled"]) ||
        get(this.props.shows, [showUUID, "subscriptionDetails", "exclusiveContentEnabled"])
    );
  };

  enablePayments = () => {
    let updateFuncs = Object.keys(this.props.shows)
      .filter(showUUID => this.props.shows[showUUID].userUUID === this.props.user.uuid)
      .map(showUUID => callback => {
        this.props.enablePayments(showUUID).then(optInResponse => {
          callback(optInResponse.status === 200 ? null : "non-200 update response", showUUID);
        });
      });
    this.setState({ isPutting: true });
    parallelLimit(updateFuncs, 5, err => {
      this.setState({ isPutting: false });
      if (err) {
        this.props.showMessageWithTimeout(
          "There was a problem turning on payments. Please try again later.",
          messageType.Error
        );
      } else {
        this.props.showMessageWithTimeout("Payments enabled.", messageType.Success);
      }
    });
  };

  render() {
    return (
      <PageWrapper noDivider={true}>
        <If condition={this.props.user.isStripeConnected}>
          <If
            condition={
              this.state.stripeStatus === "inactive" &&
              get(this.props.user, ["userAttributes", "shouldHaveStripeAccess"]) !== "false"
            }>
            <WarningPanel>
              Stripe requires additional verification for your account. Please{" "}
              <span className="fake-link" onClick={this.loginToStripe}>
                login
              </span>
              .
            </WarningPanel>
          </If>
          <If condition={this.state.stripeStatus === "pending"}>
            <WarningPanel>
              Stripe is still verifying your account. Please check back later to confirm your
              account is set up.
            </WarningPanel>
          </If>
        </If>
        <Spinner isLoading={this.props.isLoading} />
        <If condition={!this.props.user.isStripeConnected && !this.props.isLoading}>
          {this.notConnectedContent()}
        </If>
        <If condition={this.props.user.isStripeConnected && !this.props.isLoading}>
          {this.mainContent()}
        </If>
      </PageWrapper>
    );
  }

  notConnectedContent = () => {
    return (
      <Grid fluid className="monetization-page__not-connected-wrapper">
        <Row>
          <Col xs={12} md={6}>
            <div className="monetization-page__not-connected-header">
              <h1>Start Earning</h1>
            </div>
            <hr className="redcircle-divider show-page-divider" />
            <div className="monetization-page__not-connected-content">
              <p>
                To start collecting donations from your users, first you need to connect your bank
                account to RedCircle.{" "}
                <If condition={!this.state.introMoreInfo}>
                  <button
                    className="btn-link redcircle-btn-link"
                    onClick={() => {
                      this.setState({ introMoreInfo: true });
                    }}>
                    More Info
                  </button>
                </If>
              </p>
              <If condition={this.state.introMoreInfo}>
                <p>
                  RedCircle partners with{" "}
                  <ExternalLink href="https://stripe.com">Stripe</ExternalLink> to seamlessly
                  connect your bank account to the platform so you can get paid via direct deposit.
                </p>
                <p>
                  Don't worry, we can't draw money from your account, and Stripe is known for having
                  the best security on the web.
                </p>
              </If>
            </div>
            <div className="monetization-page__not-connected-button">
              <StripeConnection user={this.props.user} />
            </div>
            <div className="monetization-page__cut-info">
              By connecting your bank account you agree to the "Monetization Services for Podcast
              Creators" section of&nbsp;
              <ExternalLink href="https://redcircle.com/terms">
                RedCircle's Terms of Service
              </ExternalLink>
              . Stripe collects payment processing fees and RedCircle collects an industry-leading
              4.5% fee on tips and 12% on exclusive content subscriptions made through the platform.
              You can find more info about the fee structures{" "}
              <ExternalLink href="https://redcircle.com/pricing">here</ExternalLink> and{" "}
              <ExternalLink href="https://support.redcircle.com/en/articles/3131569-exclusive-content">
                here
              </ExternalLink>
              .
            </div>
          </Col>
        </Row>
      </Grid>
    );
  };

  handleWithdraw = () => {
    this.props.showWithdrawConfirmation(() => {
      this.props.withdrawFunds(this.props.user).then(response => {
        if (response.status === 200) {
          this.props.getStripeBalance();
          this.props.getTransactionsForUser();
        }
      });
    });
  };

  loginToStripe = () => {
    this.props.getStripeLoginLink(this.props.user).then(response => {
      if (response.status === 200) {
        window.location.href = response.json.link;
      }
    });
  };

  hasAnyBalance = () => {
    return some(this.props.balance, currencyInfo => {
      if (currencyInfo.amount && currencyInfo.amount > 0) {
        return true;
      }
    });
  };

  mainContent = () => {
    return (
      <Grid fluid>
        <Row>
          <Col xs={12}>
            <div className="flex-row-container align-center monetization-page__balance-header">
              <h1 className="no-margin">Money</h1>
              <If
                condition={
                  get(this.props.user, ["userAttributes", "shouldHaveStripeAccess"]) !== "false"
                }>
                <ContextMenu
                  className="margin-left--medium"
                  menuItems={{
                    "Log In to Stripe": this.loginToStripe,
                  }}
                />
              </If>
              <div className="spacer flex-1" />
              <div className="monetization-page__cash-out-button">
                <If condition={!this.hasAnyBalance()}>
                  <OverlayTrigger
                    defaultShow
                    overlay={
                      <Tooltip id="disabled-button-tip">
                        <strong>You must have a current balance to transfer money out.</strong>
                      </Tooltip>
                    }
                    show={null}
                    placement="bottom">
                    <div className="promotions-show-page__cross-promote-button-tipper" />
                  </OverlayTrigger>
                </If>
                <Button
                  className="btn btn-primary btn-small"
                  onClick={this.handleWithdraw}
                  disabled={!this.hasAnyBalance()}>
                  Transfer to Bank
                </Button>
              </div>
            </div>
          </Col>
        </Row>
        <Divider smallMargin />
        <BalanceIndicatorWrapper user={this.props.user} transactions={this.creatorPayments()} />
        <If condition={this.hasNoShowsWithTipsEnabled()}>
          <Row>
            <Col xs={12}>
              <div className="monetization-page__no-podcasts-opted-in">
                <Spinner isLoading={this.props.isPuttingShow} />
                <WarningPanel>
                  You don't have any podcasts accepting payments.{" "}
                  <button className="btn btn-link redcircle-btn-link" onClick={this.enablePayments}>
                    Enable payments for my podcasts.
                  </button>
                </WarningPanel>
              </div>
            </Col>
          </Row>
        </If>
        <Row className="margin-top--large">
          <Col xs={12}>
            <h3>Transactions History</h3>
            <Divider marginTop="0" marginBottom="5px" />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <TransactionTable
              transactions={this.creatorPayments()}
              noDataText={"You don't have any transactions yet. Go earn your first payment!"}
            />
          </Col>
        </Row>
      </Grid>
    );
  };
}

export default MonetizationPage;
